import React from 'react';
import { Check, Copy, Edit3, Award, GitBranch, Code2, Eye, Lock, Printer } from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';

interface BookExpandableActionsProps {
  book: Book;
  canEdit: boolean;
  userRole?: string;
  copiedUibn: boolean;
  onCopyUibn: () => void;
  onEdit?: (book: Book) => void;
  onOpenCert: () => void;
  onOpenPrintLabel?: () => void;
  onOpenEditionTree: () => void;
  onOpenEmbedBadge: () => void;
  onOpenModal: (book: Book) => void;
}

export const BookExpandableActions: React.FC<BookExpandableActionsProps> = ({
  book,
  canEdit,
  userRole,
  copiedUibn,
  onCopyUibn,
  onEdit,
  onOpenCert,
  onOpenPrintLabel,
  onOpenEditionTree,
  onOpenEmbedBadge,
  onOpenModal,
}) => {
  const isApproved = book.status === 'disetujui';
  const canModifyMetadata = userRole === 'admin' || book.status === 'draft';
  const isInReview = book.status === 'diajukan' || book.status === 'menunggu_penerbit';

  return (
    <div className="pt-2 border-t border-slate-200/80 flex flex-col lg:flex-row lg:items-center justify-between gap-2.5">
      {/* Grid of actions on mobile, flex row on tablet & desktop */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 w-full lg:w-auto">
        {/* Salin Nomor UIBN (Only for Owner / Admin) */}
        {canEdit && (
          <button
            type="button"
            onClick={onCopyUibn}
            className="px-3 py-2 sm:py-1.5 rounded-xl bg-white hover:bg-slate-100 active:bg-slate-200 text-slate-700 border border-slate-200 text-xs font-semibold flex items-center justify-center gap-1.5 transition cursor-pointer shadow-2xs"
            title="Salin Nomor UIBN Resmi"
          >
            {copiedUibn ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span className="text-emerald-700">Tersalin!</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="hidden sm:inline">Salin Nomor UIBN</span>
                <span className="sm:hidden">Salin UIBN</span>
              </>
            )}
          </button>
        )}

        {/* Edit Metadata: Allowed if Admin or Draft; Locked if In Review or Approved */}
        {canEdit && (
          canModifyMetadata && onEdit ? (
            <button
              type="button"
              onClick={() => onEdit(book)}
              className="px-3 py-2 sm:py-1.5 rounded-xl bg-indigo-50 hover:bg-indigo-100 active:bg-indigo-200 text-indigo-700 border border-indigo-200 text-xs font-semibold flex items-center justify-center gap-1.5 transition cursor-pointer"
              title="Ubah Metadata Naskah Buku"
            >
              <Edit3 className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline">Edit Metadata</span>
              <span className="sm:hidden">Edit Metadata</span>
            </button>
          ) : isInReview ? (
            <div
              className="px-2.5 py-1.5 rounded-xl bg-slate-100 text-slate-400 border border-slate-200 text-xs font-medium flex items-center justify-center gap-1.5 cursor-not-allowed select-none"
              title="Metadata buku terkunci selama proses peninjauan pengajuan UIBN"
            >
              <Lock className="w-3.5 h-3.5 shrink-0 text-slate-400" />
              <span className="hidden sm:inline">Terkunci (Peninjauan)</span>
              <span className="sm:hidden">Terkunci</span>
            </div>
          ) : null
        )}

        {/* E-Certificate Button (Only after Approved) */}
        {canEdit && isApproved && (
          <button
            type="button"
            onClick={onOpenCert}
            className="px-3 py-2 sm:py-1.5 rounded-xl bg-amber-400 hover:bg-amber-500 active:bg-amber-600 text-slate-950 text-xs font-bold flex items-center justify-center gap-1.5 transition cursor-pointer shadow-2xs border border-amber-500/40"
            title="Terbitkan / Unduh E-Sertifikat Digital PDF"
          >
            <Award className="w-3.5 h-3.5 shrink-0 text-slate-900" />
            <span className="hidden sm:inline">E-Sertifikat (PDF)</span>
            <span className="sm:hidden">E-Sertifikat</span>
          </button>
        )}

        {/* Print Label Button (Only for Book Owner/Admin after Approved) */}
        {canEdit && isApproved && onOpenPrintLabel && (
          <button
            type="button"
            onClick={onOpenPrintLabel}
            className="px-3 py-2 sm:py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-xs font-bold flex items-center justify-center gap-1.5 transition cursor-pointer shadow-xs"
            title="Cetak Label & Barcode UIBN (Printer-Friendly)"
          >
            <Printer className="w-3.5 h-3.5 shrink-0" />
            <span className="hidden sm:inline">Cetak Label</span>
            <span className="sm:hidden">Label</span>
          </button>
        )}

        {/* Silsilah Edisi Button */}
        <button
          type="button"
          onClick={onOpenEditionTree}
          className="px-3 py-2 sm:py-1.5 rounded-xl bg-indigo-50 hover:bg-indigo-100 active:bg-indigo-200 text-indigo-700 border border-indigo-200 text-xs font-semibold flex items-center justify-center gap-1.5 transition cursor-pointer"
          title="Lihat Pohon Silsilah Edisi Buku"
        >
          <GitBranch className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
          <span className="hidden sm:inline">Silsilah Edisi</span>
          <span className="sm:hidden">Silsilah</span>
        </button>

        {/* Embed Badge Button (Only after Approved) */}
        {isApproved && (
          <button
            type="button"
            onClick={onOpenEmbedBadge}
            className="col-span-2 sm:col-span-1 px-3 py-2 sm:py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 active:bg-emerald-200 text-emerald-800 border border-emerald-200 text-xs font-semibold flex items-center justify-center gap-1.5 transition cursor-pointer"
            title="Dapatkan Kode HTML/JS Widget Badge Tersemat"
          >
            <Code2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
            <span>Widget Badge</span>
          </button>
        )}
      </div>

      {/* Primary Modal Action Button */}
      <button
        type="button"
        onClick={() => onOpenModal(book)}
        className="w-full lg:w-auto px-4 py-2.5 sm:py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition cursor-pointer shadow-xs shrink-0"
      >
        <Eye className="w-3.5 h-3.5 shrink-0" />
        <span className="hidden sm:inline">
          {userRole === 'admin' ? 'Buka Modal Penuh & Kontrol Status' : 'Lihat Detail Selengkapnya'}
        </span>
        <span className="sm:hidden">
          {userRole === 'admin' ? 'Modal & Status' : 'Detail Selengkapnya'}
        </span>
      </button>
    </div>
  );
};
